import './MessageList.scss';

import { useCallback, useRef, forwardRef, useEffect } from 'react';
import { useThrottleFn } from 'ahooks';
import { isStringEmpty } from '@shared/utils';
import ScrollView from '@/ui/scroll-view';
import ListItem from './ListItem';
import MindmapModal from '../mindmap-modal';

import chatStoreManager from '@/store/chat/manager';
import ConnectionLineStore from '@/components/chat-history/connection-line/store';
import EE from '@/common/event';
import Loader from '@/common/helper/loader';
import checkStore from '@/store/checkStore';
import userStore from '@/store/user';
import { MessageContext } from './context';
import useIsAiso from '@/hooks/useIsAiso';

function MessageList(props, ref) {
  const { items = [],backBottomButtonChange, isAgent, isKnowledgeChat, knowledgeId, isMcpShare, kn_continue_chat, entrance, handleSend } = props;
  const chatStore = chatStoreManager.current;
  const mcpStore = chatStoreManager.current?.mcpStore;

  const { historyData } = chatStore;
  const isAiso = useIsAiso();

  const containerRef = useRef(null);
  const scrollContentRef = useRef(null);

  const fetchMore = useCallback(() => {
    if(isMcpShare){
      return;
    }
    if (!isStringEmpty(chatStoreManager.current.historyData.npt)) {
      chatStoreManager.current.fetchChatHistory(chatStoreManager.current.chatId);
    }
  }, []);

  const onScrollToTop = useCallback((top, behavior) => {
    if(checkStore.data.isShowCheck) return
    containerRef.current?.scrollTo({
      left: 0,
      top: top - containerRef.current.offsetTop,
      behavior,
    });
  }, []);

  const { run: onScroll } = useThrottleFn(
    event => {
      const { scrollTop } = event.target;
      if (scrollTop <= 100) {
        fetchMore();
      }
      backBottomButtonChange?.(containerRef.current.scrollHeight - containerRef.current.scrollTop - containerRef.current.clientHeight > Loader.autoScrollToBottomGap)
      EE.emit('scroll_hide_dropdown');
    },
    { wait: 500 }
  );

  useEffect(() => {
    ref.current = containerRef.current
    EE.on("chat:top", () => {
      setTimeout(() => {
        containerRef.current?.scrollTo({
          left: 0,
          top: 0,
          behavior: "smooth",
        });
      }, 200)
    })
  }, [containerRef])

  useEffect(() => {
    const handleScroll = () => {
      if(mcpStore.panelMessageId && mcpStore.currentPanelId && mcpStore.showRightPanel){
        const id = `${mcpStore.panelMessageId}_${mcpStore.currentPanelId}`;
        ConnectionLineStore.updatePoint(id);
      }
    }
    setTimeout(() => {
      handleScroll();
    }, 200);
    // 监听viewerContainerRef的resize事件
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(scrollContentRef.current);
    containerRef.current?.addEventListener('scroll', handleScroll);
    return () => {
      containerRef.current?.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    }
  }, [mcpStore.panelMessageId, mcpStore.currentPanelId, mcpStore.showRightPanel]);
  
  return (
    <div className="message-list-container size-full overflow-hidden px-4px">
      <MindmapModal />
      <ScrollView
        ref={containerRef}
        enableAutoScroll={true}
        id="message-list"
        className={[
          'flex flex-col items-center outline-none !w-auto',
          isMcpShare ? ( chatStore.mcpStore.isRightOpen ? 'pb-60px' : 'pb-100px') : 'pb-200px',
          'js-message-list pl-44px pr-36px',
        ]}
        onScroll={onScroll}
      >
        <ul 
          ref={scrollContentRef}
          className={`${chatStore.mcpStore.showRightPanel ? '' : 'sm:max-w-776px'} w-full flex flex-col`}
          style={Loader.getChatContainerStyle({ isKnowledgeChat, showRightPanel : chatStore.mcpStore.isRightOpen }, true)}
          >
          {items.map((item, index) => (
            <MessageContext.Provider
              key={item.msg_id}
              value={{
                conversationId : chatStore.chatId,
                ...item,
              }}>
                <ListItem
                  key={item.msg_id}
                  index={index}
                  isAgent={isAgent}
                  isKnowledgeChat={isKnowledgeChat}
                  knowledgeId={knowledgeId}
                  isMcpShare={isMcpShare}
                  kn_continue_chat={kn_continue_chat}
                  entrance={entrance}
                  {...item}
                  handleSend={handleSend}
                  onScrollToTop={onScrollToTop}
                />

            </MessageContext.Provider>
          ))}
        </ul>
        {
          checkStore.data.isShowCheck
          ? (
            <div className='fixed left-30000px'>
              <ul className="w-600px flex flex-col bg-white" id="message-list-ul-clone">
                <li className='h-109px w-full bg-[#f4f4f4] mb-12px flex justify-between items-center px-24px box-border'>
                  <img 
                    src="https://p3.ssl.qhimg.com/t110b9a9301b70d64f30a8a6595.png"
                    className='w-182px h-53px flex-shrink-0' 
                    alt="" 
                  />
                  <div className='h-54px'>
                    <div className='flex h-30px items-center justify-end'>
                      <img className='size-30px rounded-full mr-8px' src={userStore.showIcon} alt="" />
                      <div className='text-black text-18px whitespace-nowrap font-bold'>{userStore.showNickname}</div>
                    </div>
                    <div className='mt-2px text-14px leading-20px opacity-50 text-black text-right'>
                      与你分享对话
                    </div>
                  </div>
                </li>
                {items.filter(item => {
                  const checkedList = checkStore.data.checkList.filter(checkItem => checkItem.checked)
                  return !!checkedList.find(_item => {
                    return _item.label.includes(item.msg_id)
                  })
                }).map((item, index) => (
                  <ListItem
                    key={item.msg_id}
                    clone={true}
                    index={index}
                    isAgent={isAgent}
                    {...item}
                    onScrollToTop={onScrollToTop}
                  />
                ))}
                <li className="h-208px bg-[#f4f4f4] flex-shrink-0 flex flex-col items-center mt-16px">
                  <img 
                    src="https://p3.ssl.qhimg.com/t110b9a9301165fa8f89722675d.png" 
                    className='size-100px mt-28px'
                    alt="" 
                  />
                  <div className='mt-8px text-12px leading-14px text-[rgba(0,0,0,0.5)]'>扫描二维码</div>
                  <div className='text-12px leading-14px text-[rgba(0,0,0,0.5)]'>下载纳米AI搜索App</div>
                </li>
              </ul>
            </div>
          )
          : null
        }
      </ScrollView>
    </div>
  );
}

export default forwardRef(MessageList);
